import React, { useState } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const SignupContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: ${theme.colors.background};
  flex-direction: row;
  font-family: ${theme.typography.fontFamily};
  position: relative;
  gap: ${theme.spacing.xxxxl};
  padding: ${theme.spacing.xl};
`;

const SignupCard = styled.div`
  background-color: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.xxl};
  padding: ${theme.spacing.xxxl};
  border: 1px solid ${theme.colors.border};
  box-shadow: ${theme.shadows.lg};
  width: 100%;
  max-width: 420px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const Title = styled.h2`
  margin: 0 0 ${theme.spacing.xs} 0;
  color: ${theme.colors.text};
  text-align: center;
  font-size: ${theme.typography.fontSizes.xxxxl};
  font-weight: ${theme.typography.fontWeights.bold};
  font-family: ${theme.typography.fontFamily};
  letter-spacing: ${theme.typography.letterSpacing.normal};
`;

const Subtitle = styled.p`
  margin: 0 0 ${theme.spacing.lg} 0;
  color: ${theme.colors.textSecondary};
  text-align: center;
  font-size: ${theme.typography.fontSizes.base};
  font-family: ${theme.typography.fontFamily};
  line-height: 1.5;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

const Label = styled.label`
  display: block;
  color: ${theme.colors.text};
  font-size: ${theme.typography.fontSizes.base};
  font-weight: ${theme.typography.fontWeights.medium};
  font-family: ${theme.typography.fontFamily};
`;

const ErrorMessage = styled.div`
  color: ${theme.colors.error};
  font-size: ${theme.typography.fontSizes.sm};
  text-align: center;
  padding: ${theme.spacing.sm};
  background-color: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.sm};
  border: 1px solid ${theme.colors.error};
  font-family: ${theme.typography.fontFamily};
  font-weight: ${theme.typography.fontWeights.normal};
`;

const SuccessMessage = styled.div`
  color: ${theme.colors.success};
  font-size: ${theme.typography.fontSizes.sm};
  text-align: center;
  padding: ${theme.spacing.sm};
  background-color: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.sm};
  border: 1px solid ${theme.colors.success};
  font-family: ${theme.typography.fontFamily};
  font-weight: ${theme.typography.fontWeights.normal};
`;

const TrialBadge = styled.div`
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primaryDark} 100%);
  color: white;
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.lg};
  text-align: center;
  font-size: ${theme.typography.fontSizes.base};
  font-weight: ${theme.typography.fontWeights.bold};
  margin-bottom: ${theme.spacing.lg};
  box-shadow: ${theme.shadows.md};
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: ${theme.spacing.md} 0;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.fontSizes.sm};
  
  &:before {
    content: '✓';
    color: ${theme.colors.success};
    font-weight: bold;
    font-size: ${theme.typography.fontSizes.lg};
    flex-shrink: 0;
  }
`;

const LoginLink = styled.div`
  text-align: center;
  margin-top: ${theme.spacing.md};
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.fontSizes.sm};
  
  a {
    color: ${theme.colors.primary};
    text-decoration: none;
    font-weight: ${theme.typography.fontWeights.medium};
    cursor: pointer;
    
    &:hover {
      color: ${theme.colors.accent};
      text-decoration: underline;
    }
  }
`;

const PasswordRequirements = styled.ul`
  margin: ${theme.spacing.sm} 0 0 0;
  padding-left: ${theme.spacing.lg};
  font-size: ${theme.typography.fontSizes.sm};
  color: ${theme.colors.textSecondary};
  
  li {
    margin: ${theme.spacing.xs} 0;
  }
`;

const SignupPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();

  const validatePassword = () => {
    if (password.length < 6) {
      return 'Password must be at least 6 characters';
    }
    if (password !== confirmPassword) {
      return 'Passwords do not match';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    // Validate password
    const passwordError = validatePassword();
    if (passwordError) {
      setError(passwordError);
      setIsLoading(false);
      return;
    }

    const result = await signup(email, password, displayName);
    
    if (result.success) {
      setSuccess('Account created successfully! Starting your 7-day free trial...');
      // Redirect to main app after 2 seconds
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } else {
      setError(result.error || 'Signup failed');
    }
    
    setIsLoading(false);
  };

  return (
    <SignupContainer>
      <SignupCard>
        <TrialBadge>7-Day Free Trial - No Credit Card Required</TrialBadge>
        <Title>Create Your Account</Title>
        <Subtitle>Join and start labeling today with full access</Subtitle>
        
        <FeaturesList>
          <FeatureItem>Unlimited access during trial</FeatureItem>
          <FeatureItem>All annotation tools included</FeatureItem>
          <FeatureItem>No credit card required</FeatureItem>
          <FeatureItem>Cancel anytime</FeatureItem>
        </FeaturesList>
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Display Name (Optional)</Label>
            <Input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Your name"
            />
          </FormGroup>
          <FormGroup>
            <Label>Email</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
            />
          </FormGroup>
          <FormGroup>
            <Label>Password</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Create a password"
              required
            />
            <PasswordRequirements>
              <li>At least 6 characters</li>
            </PasswordRequirements>
          </FormGroup>
          <FormGroup>
            <Label>Confirm Password</Label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              required
            />
          </FormGroup>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && <SuccessMessage>{success}</SuccessMessage>}
          <Button type="submit" disabled={isLoading} style={{ marginTop: theme.spacing.md }}>
            {isLoading ? 'Creating Account...' : 'Start Free Trial'}
          </Button>
        </Form>
        <LoginLink>
          Already have an account? <a onClick={() => navigate('/login')}>Login</a>
        </LoginLink>
      </SignupCard>
    </SignupContainer>
  );
};

export default SignupPage;

